import { __decorate, __param, __assign, __awaiter, __generator, __spread } from 'tslib';
import { InjectionToken, Inject, PLATFORM_ID, Injectable, EventEmitter, ViewChild, Input, Output, Component, Directive, Optional, NgModule } from '@angular/core';
import { filter, first, map, switchMap } from 'rxjs/operators';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { BehaviorSubject, from } from 'rxjs';

var STRIPE_PUBLISHABLE_KEY = new InjectionToken('Stripe Publishable Key');
var STRIPE_OPTIONS = new InjectionToken('Stripe Options');

var WindowRef = /** @class */ (function () {
    function WindowRef(platformId) {
        this.platformId = platformId;
    }
    WindowRef.prototype.getNativeWindow = function () {
        if (isPlatformBrowser(this.platformId)) {
            return window;
        }
        return {};
    };
    WindowRef.ctorParameters = function () { return [
        { type: undefined, decorators: [{ type: Inject, args: [PLATFORM_ID,] }] }
    ]; };
    WindowRef = __decorate([
        Injectable(),
        __param(0, Inject(PLATFORM_ID))
    ], WindowRef);
    return WindowRef;
}());

var DocumentRef = /** @class */ (function () {
    function DocumentRef(platformId) {
        this.platformId = platformId;
    }
    DocumentRef.prototype.getNativeDocument = function () {
        if (isPlatformBrowser(this.platformId)) {
            return document;
        }
        return {};
    };
    DocumentRef.ctorParameters = function () { return [
        { type: undefined, decorators: [{ type: Inject, args: [PLATFORM_ID,] }] }
    ]; };
    DocumentRef = __decorate([
        Injectable(),
        __param(0, Inject(PLATFORM_ID))
    ], DocumentRef);
    return DocumentRef;
}());

var LazyStripeAPILoader = /** @class */ (function () {
    function LazyStripeAPILoader(platformId, window, document) {
        this.platformId = platformId;
        this.window = window;
        this.document = document;
        this.status = new BehaviorSubject({
            error: false,
            loaded: false,
            loading: false
        });
    }
    LazyStripeAPILoader.prototype.asStream = function () {
        this.load();
        return this.status.asObservable();
    };
    LazyStripeAPILoader.prototype.isReady = function () {
        return this.status.getValue().loaded;
    };
    LazyStripeAPILoader.prototype.load = function () {
        var _this = this;
        if (isPlatformServer(this.platformId)) {
            return;
        }
        var status = this.status.getValue();
        if (this.window.getNativeWindow().hasOwnProperty('Stripe')) {
            this.status.next({
                error: false,
                loaded: true,
                loading: false
            });
        }
        else if (!status.loaded && !status.loading) {
            this.status.next(__assign(__assign({}, status), { loading: true }));
            var script = this.document.getNativeDocument().createElement('script');
            script.type = 'text/javascript';
            script.async = true;
            script.defer = true;
            script.src = 'https://js.stripe.com/v3/';
            script.onload = function () {
                _this.status.next({
                    error: false,
                    loaded: true,
                    loading: false
                });
            };
            script.onerror = function () {
                _this.status.next({
                    error: true,
                    loaded: false,
                    loading: false
                });
            };
            this.document.getNativeDocument().body.appendChild(script);
        }
    };
    LazyStripeAPILoader.ctorParameters = function () { return [
        { type: undefined, decorators: [{ type: Inject, args: [PLATFORM_ID,] }] },
        { type: WindowRef },
        { type: DocumentRef }
    ]; };
    LazyStripeAPILoader = __decorate([
        Injectable(),
        __param(0, Inject(PLATFORM_ID))
    ], LazyStripeAPILoader);
    return LazyStripeAPILoader;
}());

var StripeInstance = /** @class */ (function () {
    function StripeInstance(loader, window, key, options) {
        var _this = this;
        this.loader = loader;
        this.window = window;
        this.key = key;
        this.options = options;
        this.stripe$ = new BehaviorSubject(undefined);
        this.stripe = this.stripe$
            .asObservable()
            .pipe(filter(function (stripe) { return Boolean(stripe); }));
        this.loader
            .asStream()
            .pipe(filter(function (status) { return status.loaded === true; }), first(), map(function () { return _this.window.getNativeWindow().Stripe; }))
            .subscribe(function (stripeInstance) {
            var stripe = _this.options
                ? stripeInstance(_this.key, _this.options)
                : stripeInstance(_this.key);
            _this.stripe$.next(stripe);
        });
    }
    StripeInstance.prototype.getInstance = function () {
        return this.stripe$.getValue();
    };
    StripeInstance.prototype.elements = function (options) {
        return this.stripe$.asObservable().pipe(filter(function (stripe) { return Boolean(stripe); }), map(function (stripe) { return stripe.elements(options); }), first());
    };
    StripeInstance.prototype.redirectToCheckout = function (options) {
        return this.stripe.pipe(switchMap(function (stripe) { return from(stripe.redirectToCheckout(options)); }), first());
    };
    StripeInstance.prototype.confirmAuBecsDebitPayment = function (clientSecret, data) {
        return this.stripe.pipe(switchMap(function (stripe) {
            return from(stripe.confirmAuBecsDebitPayment(clientSecret, data));
        }), first());
    };
    StripeInstance.prototype.confirmBancontactPayment = function (clientSecret, data, options) {
        return this.stripe.pipe(switchMap(function (stripe) {
            return from(stripe.confirmBancontactPayment(clientSecret, data, options));
        }), first());
    };
    StripeInstance.prototype.confirmCardPayment = function (clientSecret, data, options) {
        return this.stripe.pipe(switchMap(function (stripe) {
            return from(stripe.confirmCardPayment(clientSecret, data, options));
        }), first());
    };
    StripeInstance.prototype.confirmEpsPayment = function (clientSecret, data, options) {
        return this.stripe.pipe(switchMap(function (stripe) {
            return from(stripe.confirmEpsPayment(clientSecret, data, options));
        }), first());
    };
    StripeInstance.prototype.confirmFpxPayment = function (clientSecret, data, options) {
        return this.stripe.pipe(switchMap(function (stripe) {
            return from(stripe.confirmFpxPayment(clientSecret, data, options));
        }), first());
    };
    StripeInstance.prototype.confirmGiropayPayment = function (clientSecret, data, options) {
        return this.stripe.pipe(switchMap(function (stripe) {
            return from(stripe.confirmGiropayPayment(clientSecret, data, options));
        }), first());
    };
    StripeInstance.prototype.confirmIdealPayment = function (clientSecret, data, options) {
        return this.stripe.pipe(switchMap(function (stripe) {
            return from(stripe.confirmIdealPayment(clientSecret, data, options));
        }), first());
    };
    StripeInstance.prototype.confirmP24Payment = function (clientSecret, data, options) {
        return this.stripe.pipe(switchMap(function (stripe) {
            return from(stripe.confirmP24Payment(clientSecret, data, options));
        }), first());
    };
    StripeInstance.prototype.confirmSepaDebitPayment = function (clientSecret, data) {
        return this.stripe.pipe(switchMap(function (stripe) {
            return from(stripe.confirmSepaDebitPayment(clientSecret, data));
        }), first());
    };
    StripeInstance.prototype.handleCardAction = function (clientSecret) {
        return this.stripe.pipe(switchMap(function (stripe) { return from(stripe.handleCardAction(clientSecret)); }), first());
    };
    StripeInstance.prototype.createPaymentMethod = function (paymentMethodData) {
        return this.stripe.pipe(switchMap(function (stripe) {
            return from(stripe.createPaymentMethod(paymentMethodData));
        }), first());
    };
    StripeInstance.prototype.retrievePaymentIntent = function (clientSecret) {
        return this.stripe.pipe(switchMap(function (stripe) { return from(stripe.retrievePaymentIntent(clientSecret)); }), first());
    };
    StripeInstance.prototype.confirmAuBecsDebitSetup = function (clientSecret, data) {
        return this.stripe.pipe(switchMap(function (stripe) {
            return from(stripe.confirmAuBecsDebitSetup(clientSecret, data));
        }), first());
    };
    StripeInstance.prototype.confirmCardSetup = function (clientSecret, data, options) {
        return this.stripe.pipe(switchMap(function (stripe) {
            return from(stripe.confirmCardSetup(clientSecret, data, options));
        }), first());
    };
    StripeInstance.prototype.confirmSepaDebitSetup = function (clientSecret, data) {
        return this.stripe.pipe(switchMap(function (stripe) {
            return from(stripe.confirmSepaDebitSetup(clientSecret, data));
        }), first());
    };
    StripeInstance.prototype.retrieveSetupIntent = function (clientSecret) {
        return this.stripe.pipe(switchMap(function (stripe) { return from(stripe.confirmSepaDebitSetup(clientSecret)); }), first());
    };
    StripeInstance.prototype.paymentRequest = function (options) {
        var stripe = this.getInstance();
        return stripe ? stripe.paymentRequest(options) : undefined;
    };
    StripeInstance.prototype.createToken = function (tokenType, data) {
        return this.stripe.pipe(switchMap(function (stripe) { return from(stripe.createToken(tokenType, data)); }), first());
    };
    StripeInstance.prototype.createSource = function (a, b) {
        return this.stripe.pipe(switchMap(function (stripe) { return from(stripe.createSource(a, b)); }), first());
    };
    StripeInstance.prototype.retrieveSource = function (source) {
        return this.stripe.pipe(switchMap(function (stripe) { return from(stripe.retrieveSource(source)); }), first());
    };
    /**
     * @deprecated
     */
    StripeInstance.prototype.handleCardPayment = function (clientSecret, element, data) {
        return this.stripe.pipe(switchMap(function (stripe) {
            return from(stripe.handleCardPayment(clientSecret, element, data));
        }), first());
    };
    /**
     * @deprecated
     */
    StripeInstance.prototype.confirmPaymentIntent = function (clientSecret, element, data) {
        return this.stripe.pipe(switchMap(function (stripe) {
            return from(stripe.confirmPaymentIntent(clientSecret, element, data));
        }), first());
    };
    /**
     * @deprecated
     */
    StripeInstance.prototype.handleCardSetup = function (clientSecret, element, data) {
        return this.stripe.pipe(switchMap(function (stripe) {
            return from(stripe.handleCardSetup(clientSecret, element, data));
        }), first());
    };
    /**
     * @deprecated
     */
    StripeInstance.prototype.confirmSetupIntent = function (clientSecret, element, data) {
        return this.stripe.pipe(switchMap(function (stripe) {
            return from(stripe.confirmSetupIntent(clientSecret, element, data));
        }), first());
    };
    /**
     * @deprecated
     */
    StripeInstance.prototype.handleFpxPayment = function (clientSecret, element, data) {
        return this.stripe.pipe(switchMap(function (stripe) {
            return from(stripe.handleFpxPayment(clientSecret, element, data));
        }), first());
    };
    return StripeInstance;
}());

var StripeService = /** @class */ (function () {
    function StripeService(key, options, loader, window) {
        this.key = key;
        this.options = options;
        this.loader = loader;
        this.window = window;
        if (key) {
            this.stripe = new StripeInstance(this.loader, this.window, key, options);
        }
    }
    StripeService.prototype.getStripeReference = function () {
        var _this = this;
        return this.loader.asStream().pipe(filter(function (status) { return status.loaded === true; }), map(function () { return _this.window.getNativeWindow().Stripe; }));
    };
    StripeService.prototype.getInstance = function () {
        return this.stripe.getInstance();
    };
    StripeService.prototype.setKey = function (key, options) {
        return this.changeKey(key, options);
    };
    StripeService.prototype.changeKey = function (key, options) {
        this.stripe = new StripeInstance(this.loader, this.window, key, options);
        return this.stripe;
    };
    StripeService.prototype.elements = function (options) {
        return this.stripe.elements(options);
    };
    StripeService.prototype.redirectToCheckout = function (options) {
        return this.stripe.redirectToCheckout(options);
    };
    StripeService.prototype.confirmAuBecsDebitPayment = function (clientSecret, data) {
        return this.stripe.confirmAuBecsDebitPayment(clientSecret, data);
    };
    StripeService.prototype.confirmBancontactPayment = function (clientSecret, data, options) {
        return this.stripe.confirmBancontactPayment(clientSecret, data, options);
    };
    StripeService.prototype.confirmCardPayment = function (clientSecret, data, options) {
        return this.stripe.confirmCardPayment(clientSecret, data, options);
    };
    StripeService.prototype.confirmEpsPayment = function (clientSecret, data, options) {
        return this.stripe.confirmEpsPayment(clientSecret, data, options);
    };
    StripeService.prototype.confirmFpxPayment = function (clientSecret, data, options) {
        return this.stripe.confirmFpxPayment(clientSecret, data, options);
    };
    StripeService.prototype.confirmGiropayPayment = function (clientSecret, data, options) {
        return this.stripe.confirmGiropayPayment(clientSecret, data, options);
    };
    StripeService.prototype.confirmIdealPayment = function (clientSecret, data, options) {
        return this.stripe.confirmIdealPayment(clientSecret, data, options);
    };
    StripeService.prototype.confirmP24Payment = function (clientSecret, data, options) {
        return this.stripe.confirmP24Payment(clientSecret, data, options);
    };
    StripeService.prototype.confirmSepaDebitPayment = function (clientSecret, data) {
        return this.stripe.confirmSepaDebitPayment(clientSecret, data);
    };
    StripeService.prototype.handleCardAction = function (clientSecret) {
        return this.stripe.handleCardAction(clientSecret);
    };
    StripeService.prototype.createPaymentMethod = function (paymentMethodData) {
        return this.stripe.createPaymentMethod(paymentMethodData);
    };
    StripeService.prototype.retrievePaymentIntent = function (clientSecret) {
        return this.stripe.retrievePaymentIntent(clientSecret);
    };
    StripeService.prototype.confirmAuBecsDebitSetup = function (clientSecret, data) {
        return this.stripe.confirmAuBecsDebitSetup(clientSecret, data);
    };
    StripeService.prototype.confirmCardSetup = function (clientSecret, data, options) {
        return this.stripe.confirmCardSetup(clientSecret, data, options);
    };
    StripeService.prototype.confirmSepaDebitSetup = function (clientSecret, data) {
        return this.stripe.confirmSepaDebitSetup(clientSecret, data);
    };
    StripeService.prototype.retrieveSetupIntent = function (clientSecret) {
        return this.stripe.retrieveSetupIntent(clientSecret);
    };
    StripeService.prototype.paymentRequest = function (options) {
        return this.stripe.paymentRequest(options);
    };
    StripeService.prototype.createToken = function (tokenType, data) {
        return this.stripe.createToken(tokenType, data);
    };
    StripeService.prototype.createSource = function (a, b) {
        return this.stripe.createSource(a, b);
    };
    StripeService.prototype.retrieveSource = function (source) {
        return this.stripe.retrieveSource(source);
    };
    /**
     * @deprecated
     */
    StripeService.prototype.handleCardPayment = function (clientSecret, element, data) {
        return this.stripe.handleCardPayment(clientSecret, element, data);
    };
    /**
     * @deprecated
     */
    StripeService.prototype.confirmPaymentIntent = function (clientSecret, element, data) {
        return this.stripe.confirmPaymentIntent(clientSecret, element, data);
    };
    /**
     * @deprecated
     */
    StripeService.prototype.handleCardSetup = function (clientSecret, element, data) {
        return this.stripe.handleCardSetup(clientSecret, element, data);
    };
    /**
     * @deprecated
     */
    StripeService.prototype.confirmSetupIntent = function (clientSecret, element, data) {
        return this.stripe.confirmSetupIntent(clientSecret, element, data);
    };
    /**
     * @deprecated
     */
    StripeService.prototype.handleFpxPayment = function (clientSecret, element, data) {
        return this.stripe.handleFpxPayment(clientSecret, element, data);
    };
    StripeService.ctorParameters = function () { return [
        { type: String, decorators: [{ type: Inject, args: [STRIPE_PUBLISHABLE_KEY,] }] },
        { type: undefined, decorators: [{ type: Inject, args: [STRIPE_OPTIONS,] }] },
        { type: LazyStripeAPILoader },
        { type: WindowRef }
    ]; };
    StripeService = __decorate([
        Injectable(),
        __param(0, Inject(STRIPE_PUBLISHABLE_KEY)),
        __param(1, Inject(STRIPE_OPTIONS))
    ], StripeService);
    return StripeService;
}());

var StripeElementsService = /** @class */ (function () {
    function StripeElementsService(stripeService) {
        this.stripeService = stripeService;
    }
    StripeElementsService.prototype.elements = function (stripe, options) {
        if (options === void 0) { options = {}; }
        if (stripe) {
            if (Object.keys(options).length > 0) {
                return stripe.elements(options);
            }
            return stripe.elements();
        }
        else {
            if (Object.keys(options).length > 0) {
                return this.stripeService.elements(options);
            }
            return this.stripeService.elements();
        }
    };
    StripeElementsService.prototype.paymentRequest = function (stripe, options) {
        return stripe
            ? stripe.paymentRequest(options)
            : this.stripeService.paymentRequest(options);
    };
    StripeElementsService.prototype.mergeOptions = function (options, containerClass) {
        if (!containerClass || (options && options.classes)) {
            return options || {};
        }
        if (!options || !options.classes) {
            return __assign(__assign({}, (options || {})), { classes: {
                    base: containerClass,
                    complete: containerClass + "--complete",
                    empty: containerClass + "--empty",
                    focus: containerClass + "--focus",
                    invalid: containerClass + "--invalid",
                    webkitAutoFill: containerClass + "--webkit-autoFill"
                } });
        }
        return options || {};
    };
    StripeElementsService.ctorParameters = function () { return [
        { type: StripeService }
    ]; };
    StripeElementsService = __decorate([
        Injectable()
    ], StripeElementsService);
    return StripeElementsService;
}());

var StripeCardComponent = /** @class */ (function () {
    function StripeCardComponent(stripeElementsService) {
        this.stripeElementsService = stripeElementsService;
        this.load = new EventEmitter();
        this.blur = new EventEmitter();
        this.change = new EventEmitter();
        this.focus = new EventEmitter();
        this.ready = new EventEmitter();
        this.escape = new EventEmitter();
        this.state = 'notready';
    }
    StripeCardComponent.prototype.ngOnChanges = function (changes) {
        return __awaiter(this, void 0, void 0, function () {
            var options, updateElements, _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        this.state = 'starting';
                        options = this.stripeElementsService.mergeOptions(this.options, this.containerClass);
                        updateElements = false;
                        if (!(changes.elementsOptions || changes.stripe || !this.elements)) return [3 /*break*/, 2];
                        _a = this;
                        return [4 /*yield*/, this.stripeElementsService
                                .elements(this.stripe, this.elementsOptions)
                                .toPromise()];
                    case 1:
                        _a.elements = _b.sent();
                        updateElements = true;
                        _b.label = 2;
                    case 2:
                        if (changes.options ||
                            changes.containerClass ||
                            !this.element ||
                            updateElements) {
                            if (this.element && !updateElements) {
                                this.update(options);
                            }
                            else if (this.elements && updateElements) {
                                this.createElement(options);
                            }
                        }
                        this.state = 'ready';
                        return [2 /*return*/];
                }
            });
        });
    };
    StripeCardComponent.prototype.ngOnInit = function () {
        return __awaiter(this, void 0, void 0, function () {
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        if (!(this.state === 'notready')) return [3 /*break*/, 2];
                        this.state = 'starting';
                        _a = this;
                        return [4 /*yield*/, this.stripeElementsService
                                .elements(this.stripe)
                                .toPromise()];
                    case 1:
                        _a.elements = _b.sent();
                        this.createElement();
                        this.state = 'ready';
                        _b.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    StripeCardComponent.prototype.ngOnDestroy = function () {
        if (this.element) {
            this.element.destroy();
        }
    };
    StripeCardComponent.prototype.update = function (options) {
        this.element.update(options);
    };
    /**
     * @deprecated
     */
    StripeCardComponent.prototype.getCard = function () {
        return this.element;
    };
    StripeCardComponent.prototype.createElement = function (options) {
        var _this = this;
        if (options === void 0) { options = {}; }
        this.element = this.elements.create('card', options);
        this.element.on('change', function (ev) { return _this.change.emit(ev); });
        this.element.on('blur', function () { return _this.blur.emit(); });
        this.element.on('focus', function () { return _this.focus.emit(); });
        this.element.on('ready', function () { return _this.ready.emit(); });
        this.element.on('escape', function () { return _this.escape.emit(); });
        this.element.mount(this.stripeElementRef.nativeElement);
        this.load.emit(this.element);
    };
    StripeCardComponent.ctorParameters = function () { return [
        { type: StripeElementsService }
    ]; };
    __decorate([
        ViewChild('stripeElementRef', { static: false })
    ], StripeCardComponent.prototype, "stripeElementRef", void 0);
    __decorate([
        Input()
    ], StripeCardComponent.prototype, "containerClass", void 0);
    __decorate([
        Input()
    ], StripeCardComponent.prototype, "options", void 0);
    __decorate([
        Input()
    ], StripeCardComponent.prototype, "elementsOptions", void 0);
    __decorate([
        Input()
    ], StripeCardComponent.prototype, "stripe", void 0);
    __decorate([
        Output()
    ], StripeCardComponent.prototype, "load", void 0);
    __decorate([
        Output()
    ], StripeCardComponent.prototype, "blur", void 0);
    __decorate([
        Output()
    ], StripeCardComponent.prototype, "change", void 0);
    __decorate([
        Output()
    ], StripeCardComponent.prototype, "focus", void 0);
    __decorate([
        Output()
    ], StripeCardComponent.prototype, "ready", void 0);
    __decorate([
        Output()
    ], StripeCardComponent.prototype, "escape", void 0);
    StripeCardComponent = __decorate([
        Component({
            selector: 'ngx-stripe-card',
            template: "<div class=\"field\" #stripeElementRef></div>"
        })
    ], StripeCardComponent);
    return StripeCardComponent;
}());

var StripeCardGroupDirective = /** @class */ (function () {
    function StripeCardGroupDirective(stripeElementsService) {
        this.stripeElementsService = stripeElementsService;
        this.elements = new EventEmitter();
        this.state = 'notready';
    }
    StripeCardGroupDirective.prototype.ngOnChanges = function (changes) {
        return __awaiter(this, void 0, void 0, function () {
            var elementsOptions, stripe, _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        this.state = 'starting';
                        elementsOptions = this.elementsOptions;
                        stripe = this.stripe;
                        if (!(changes.elementsOptions || changes.stripe || !this._elements)) return [3 /*break*/, 2];
                        _a = this;
                        return [4 /*yield*/, this.stripeElementsService
                                .elements(stripe, elementsOptions)
                                .toPromise()];
                    case 1:
                        _a._elements = _b.sent();
                        this.elements.emit(this._elements);
                        _b.label = 2;
                    case 2:
                        this.state = 'ready';
                        return [2 /*return*/];
                }
            });
        });
    };
    StripeCardGroupDirective.prototype.ngOnInit = function () {
        return __awaiter(this, void 0, void 0, function () {
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        if (!(this.state === 'notready')) return [3 /*break*/, 2];
                        this.state = 'starting';
                        _a = this;
                        return [4 /*yield*/, this.stripeElementsService
                                .elements(this.stripe)
                                .toPromise()];
                    case 1:
                        _a._elements = _b.sent();
                        this.elements.emit(this._elements);
                        this.state = 'ready';
                        _b.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    StripeCardGroupDirective.ctorParameters = function () { return [
        { type: StripeElementsService }
    ]; };
    __decorate([
        Input()
    ], StripeCardGroupDirective.prototype, "elementsOptions", void 0);
    __decorate([
        Input()
    ], StripeCardGroupDirective.prototype, "stripe", void 0);
    __decorate([
        Output()
    ], StripeCardGroupDirective.prototype, "elements", void 0);
    StripeCardGroupDirective = __decorate([
        Directive({
            selector: 'ngx-stripe-card-group,[ngxStripeCardGroup]'
        })
    ], StripeCardGroupDirective);
    return StripeCardGroupDirective;
}());

var StripeCardNumberComponent = /** @class */ (function () {
    function StripeCardNumberComponent(stripeElementsService, cardGroup) {
        this.stripeElementsService = stripeElementsService;
        this.cardGroup = cardGroup;
        this.load = new EventEmitter();
        this.blur = new EventEmitter();
        this.change = new EventEmitter();
        this.focus = new EventEmitter();
        this.ready = new EventEmitter();
        this.escape = new EventEmitter();
    }
    StripeCardNumberComponent.prototype.ngOnChanges = function (changes) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                if (changes.options || changes.containerClass) {
                    this.setupElement('options');
                }
                return [2 /*return*/];
            });
        });
    };
    StripeCardNumberComponent.prototype.ngOnInit = function () {
        var _this = this;
        if (this.cardGroup) {
            this.cardGroupSubscription = this.cardGroup.elements.subscribe(function (elements) {
                _this.elements = elements;
                _this.setupElement('elements');
            });
        }
        else {
            throw new Error('StripeCardNumberComponent must have StripeCardGroupDirective parent');
        }
    };
    StripeCardNumberComponent.prototype.ngOnDestroy = function () {
        if (this.element) {
            this.element.destroy();
        }
        if (this.cardGroupSubscription) {
            this.cardGroupSubscription.unsubscribe();
        }
    };
    StripeCardNumberComponent.prototype.update = function (options) {
        this.element.update(options);
    };
    /**
     * @deprecated
     */
    StripeCardNumberComponent.prototype.getCardNumber = function () {
        return this.element;
    };
    StripeCardNumberComponent.prototype.setupElement = function (source) {
        var _this = this;
        var options = this.stripeElementsService.mergeOptions(this.options, this.containerClass);
        if (this.element && source === 'options') {
            this.update(options);
        }
        else if (this.elements && source === 'elements') {
            this.element = this.elements.create('cardNumber', options);
            this.element.on('change', function (ev) { return _this.change.emit(ev); });
            this.element.on('blur', function () { return _this.blur.emit(); });
            this.element.on('focus', function () { return _this.focus.emit(); });
            this.element.on('ready', function () { return _this.ready.emit(); });
            this.element.on('escape', function () { return _this.escape.emit(); });
            this.element.mount(this.stripeElementRef.nativeElement);
            this.load.emit(this.element);
        }
    };
    StripeCardNumberComponent.ctorParameters = function () { return [
        { type: StripeElementsService },
        { type: StripeCardGroupDirective, decorators: [{ type: Optional }] }
    ]; };
    __decorate([
        ViewChild('stripeElementRef', { static: false })
    ], StripeCardNumberComponent.prototype, "stripeElementRef", void 0);
    __decorate([
        Input()
    ], StripeCardNumberComponent.prototype, "containerClass", void 0);
    __decorate([
        Input()
    ], StripeCardNumberComponent.prototype, "options", void 0);
    __decorate([
        Output()
    ], StripeCardNumberComponent.prototype, "load", void 0);
    __decorate([
        Output()
    ], StripeCardNumberComponent.prototype, "blur", void 0);
    __decorate([
        Output()
    ], StripeCardNumberComponent.prototype, "change", void 0);
    __decorate([
        Output()
    ], StripeCardNumberComponent.prototype, "focus", void 0);
    __decorate([
        Output()
    ], StripeCardNumberComponent.prototype, "ready", void 0);
    __decorate([
        Output()
    ], StripeCardNumberComponent.prototype, "escape", void 0);
    StripeCardNumberComponent = __decorate([
        Component({
            selector: 'ngx-stripe-card-number',
            template: "<div class=\"field\" #stripeElementRef></div>"
        }),
        __param(1, Optional())
    ], StripeCardNumberComponent);
    return StripeCardNumberComponent;
}());

var StripeCardExpiryComponent = /** @class */ (function () {
    function StripeCardExpiryComponent(stripeElementsService, cardGroup) {
        this.stripeElementsService = stripeElementsService;
        this.cardGroup = cardGroup;
        this.load = new EventEmitter();
        this.blur = new EventEmitter();
        this.change = new EventEmitter();
        this.focus = new EventEmitter();
        this.ready = new EventEmitter();
        this.escape = new EventEmitter();
    }
    StripeCardExpiryComponent.prototype.ngOnChanges = function (changes) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                if (changes.options || changes.containerClass) {
                    this.setupElement('options');
                }
                return [2 /*return*/];
            });
        });
    };
    StripeCardExpiryComponent.prototype.ngOnInit = function () {
        var _this = this;
        if (this.cardGroup) {
            this.cardGroupSubscription = this.cardGroup.elements.subscribe(function (elements) {
                _this.elements = elements;
                _this.setupElement('elements');
            });
        }
        else {
            throw new Error('StripeCardExpiryComponent must have StripeCardGroupDirective parent');
        }
    };
    StripeCardExpiryComponent.prototype.ngOnDestroy = function () {
        if (this.element) {
            this.element.destroy();
        }
        if (this.cardGroupSubscription) {
            this.cardGroupSubscription.unsubscribe();
        }
    };
    StripeCardExpiryComponent.prototype.update = function (options) {
        this.element.update(options);
    };
    /**
     * @deprecated
     */
    StripeCardExpiryComponent.prototype.getCardExpiry = function () {
        return this.element;
    };
    StripeCardExpiryComponent.prototype.setupElement = function (source) {
        var _this = this;
        var options = this.stripeElementsService.mergeOptions(this.options, this.containerClass);
        if (this.element && source === 'options') {
            this.update(options);
        }
        else if (this.elements && source === 'elements') {
            this.element = this.elements.create('cardExpiry', options);
            this.element.on('change', function (ev) { return _this.change.emit(ev); });
            this.element.on('blur', function () { return _this.blur.emit(); });
            this.element.on('focus', function () { return _this.focus.emit(); });
            this.element.on('ready', function () { return _this.ready.emit(); });
            this.element.on('escape', function () { return _this.escape.emit(); });
            this.element.mount(this.stripeElementRef.nativeElement);
            this.load.emit(this.element);
        }
    };
    StripeCardExpiryComponent.ctorParameters = function () { return [
        { type: StripeElementsService },
        { type: StripeCardGroupDirective, decorators: [{ type: Optional }] }
    ]; };
    __decorate([
        ViewChild('stripeElementRef', { static: false })
    ], StripeCardExpiryComponent.prototype, "stripeElementRef", void 0);
    __decorate([
        Input()
    ], StripeCardExpiryComponent.prototype, "containerClass", void 0);
    __decorate([
        Input()
    ], StripeCardExpiryComponent.prototype, "options", void 0);
    __decorate([
        Output()
    ], StripeCardExpiryComponent.prototype, "load", void 0);
    __decorate([
        Output()
    ], StripeCardExpiryComponent.prototype, "blur", void 0);
    __decorate([
        Output()
    ], StripeCardExpiryComponent.prototype, "change", void 0);
    __decorate([
        Output()
    ], StripeCardExpiryComponent.prototype, "focus", void 0);
    __decorate([
        Output()
    ], StripeCardExpiryComponent.prototype, "ready", void 0);
    __decorate([
        Output()
    ], StripeCardExpiryComponent.prototype, "escape", void 0);
    StripeCardExpiryComponent = __decorate([
        Component({
            selector: 'ngx-stripe-card-expiry',
            template: "<div class=\"field\" #stripeElementRef></div>"
        }),
        __param(1, Optional())
    ], StripeCardExpiryComponent);
    return StripeCardExpiryComponent;
}());

var StripeCardCvcComponent = /** @class */ (function () {
    function StripeCardCvcComponent(stripeElementsService, cardGroup) {
        this.stripeElementsService = stripeElementsService;
        this.cardGroup = cardGroup;
        this.load = new EventEmitter();
        this.blur = new EventEmitter();
        this.change = new EventEmitter();
        this.focus = new EventEmitter();
        this.ready = new EventEmitter();
        this.escape = new EventEmitter();
    }
    StripeCardCvcComponent.prototype.ngOnChanges = function (changes) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                if (changes.options || changes.containerClass) {
                    this.setupElement('options');
                }
                return [2 /*return*/];
            });
        });
    };
    StripeCardCvcComponent.prototype.ngOnInit = function () {
        var _this = this;
        if (this.cardGroup) {
            this.cardGroupSubscription = this.cardGroup.elements.subscribe(function (elements) {
                _this.elements = elements;
                _this.setupElement('elements');
            });
        }
        else {
            throw new Error('StripeCardCvcComponent must have StripeCardGroupDirective parent');
        }
    };
    StripeCardCvcComponent.prototype.ngOnDestroy = function () {
        if (this.element) {
            this.element.destroy();
        }
        if (this.cardGroupSubscription) {
            this.cardGroupSubscription.unsubscribe();
        }
    };
    StripeCardCvcComponent.prototype.update = function (options) {
        this.element.update(options);
    };
    /**
     * @deprecated
     */
    StripeCardCvcComponent.prototype.getCardCvc = function () {
        return this.element;
    };
    StripeCardCvcComponent.prototype.setupElement = function (source) {
        var _this = this;
        var options = this.stripeElementsService.mergeOptions(this.options, this.containerClass);
        if (this.element && source === 'options') {
            this.update(options);
        }
        else if (this.elements && source === 'elements') {
            this.element = this.elements.create('cardCvc', options);
            this.element.on('change', function (ev) { return _this.change.emit(ev); });
            this.element.on('blur', function () { return _this.blur.emit(); });
            this.element.on('focus', function () { return _this.focus.emit(); });
            this.element.on('ready', function () { return _this.ready.emit(); });
            this.element.on('escape', function () { return _this.escape.emit(); });
            this.element.mount(this.stripeElementRef.nativeElement);
            this.load.emit(this.element);
        }
    };
    StripeCardCvcComponent.ctorParameters = function () { return [
        { type: StripeElementsService },
        { type: StripeCardGroupDirective, decorators: [{ type: Optional }] }
    ]; };
    __decorate([
        ViewChild('stripeElementRef', { static: false })
    ], StripeCardCvcComponent.prototype, "stripeElementRef", void 0);
    __decorate([
        Input()
    ], StripeCardCvcComponent.prototype, "containerClass", void 0);
    __decorate([
        Input()
    ], StripeCardCvcComponent.prototype, "options", void 0);
    __decorate([
        Output()
    ], StripeCardCvcComponent.prototype, "load", void 0);
    __decorate([
        Output()
    ], StripeCardCvcComponent.prototype, "blur", void 0);
    __decorate([
        Output()
    ], StripeCardCvcComponent.prototype, "change", void 0);
    __decorate([
        Output()
    ], StripeCardCvcComponent.prototype, "focus", void 0);
    __decorate([
        Output()
    ], StripeCardCvcComponent.prototype, "ready", void 0);
    __decorate([
        Output()
    ], StripeCardCvcComponent.prototype, "escape", void 0);
    StripeCardCvcComponent = __decorate([
        Component({
            selector: 'ngx-stripe-card-cvc',
            template: "<div class=\"field\" #stripeElementRef></div>"
        }),
        __param(1, Optional())
    ], StripeCardCvcComponent);
    return StripeCardCvcComponent;
}());

var StripeFpxBankComponent = /** @class */ (function () {
    function StripeFpxBankComponent(stripeElementsService) {
        this.stripeElementsService = stripeElementsService;
        this.load = new EventEmitter();
        this.blur = new EventEmitter();
        this.change = new EventEmitter();
        this.focus = new EventEmitter();
        this.ready = new EventEmitter();
        this.escape = new EventEmitter();
        this.state = 'notready';
    }
    StripeFpxBankComponent.prototype.ngOnChanges = function (changes) {
        return __awaiter(this, void 0, void 0, function () {
            var options, updateElements, _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        this.state = 'starting';
                        options = this.stripeElementsService.mergeOptions(this.options, this.containerClass);
                        updateElements = false;
                        if (!(changes.elementsOptions || changes.stripe || !this.elements)) return [3 /*break*/, 2];
                        _a = this;
                        return [4 /*yield*/, this.stripeElementsService
                                .elements(this.stripe, this.elementsOptions)
                                .toPromise()];
                    case 1:
                        _a.elements = _b.sent();
                        updateElements = true;
                        _b.label = 2;
                    case 2:
                        if (changes.options ||
                            changes.containerClass ||
                            !this.element ||
                            updateElements) {
                            if (this.element && !updateElements) {
                                this.update(options);
                            }
                            else if (this.elements && updateElements) {
                                this.createElement(options);
                            }
                        }
                        this.state = 'ready';
                        return [2 /*return*/];
                }
            });
        });
    };
    StripeFpxBankComponent.prototype.ngOnInit = function () {
        return __awaiter(this, void 0, void 0, function () {
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        if (!(this.state === 'notready')) return [3 /*break*/, 2];
                        this.state = 'starting';
                        _a = this;
                        return [4 /*yield*/, this.stripeElementsService
                                .elements(this.stripe)
                                .toPromise()];
                    case 1:
                        _a.elements = _b.sent();
                        this.createElement();
                        this.state = 'ready';
                        _b.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    StripeFpxBankComponent.prototype.ngOnDestroy = function () {
        if (this.element) {
            this.element.destroy();
        }
    };
    StripeFpxBankComponent.prototype.update = function (options) {
        this.element.update(options);
    };
    /**
     * @deprecated
     */
    StripeFpxBankComponent.prototype.getFpxBank = function () {
        return this.element;
    };
    StripeFpxBankComponent.prototype.createElement = function (options) {
        var _this = this;
        if (options === void 0) { options = { accountHolderType: 'individual' }; }
        this.element = this.elements.create('fpxBank', options);
        this.element.on('change', function (ev) { return _this.change.emit(ev); });
        this.element.on('blur', function () { return _this.blur.emit(); });
        this.element.on('focus', function () { return _this.focus.emit(); });
        this.element.on('ready', function () { return _this.ready.emit(); });
        this.element.on('escape', function () { return _this.escape.emit(); });
        this.element.mount(this.stripeElementRef.nativeElement);
        this.load.emit(this.element);
    };
    StripeFpxBankComponent.ctorParameters = function () { return [
        { type: StripeElementsService }
    ]; };
    __decorate([
        ViewChild('stripeElementRef', { static: false })
    ], StripeFpxBankComponent.prototype, "stripeElementRef", void 0);
    __decorate([
        Input()
    ], StripeFpxBankComponent.prototype, "containerClass", void 0);
    __decorate([
        Input()
    ], StripeFpxBankComponent.prototype, "options", void 0);
    __decorate([
        Input()
    ], StripeFpxBankComponent.prototype, "elementsOptions", void 0);
    __decorate([
        Input()
    ], StripeFpxBankComponent.prototype, "stripe", void 0);
    __decorate([
        Output()
    ], StripeFpxBankComponent.prototype, "load", void 0);
    __decorate([
        Output()
    ], StripeFpxBankComponent.prototype, "blur", void 0);
    __decorate([
        Output()
    ], StripeFpxBankComponent.prototype, "change", void 0);
    __decorate([
        Output()
    ], StripeFpxBankComponent.prototype, "focus", void 0);
    __decorate([
        Output()
    ], StripeFpxBankComponent.prototype, "ready", void 0);
    __decorate([
        Output()
    ], StripeFpxBankComponent.prototype, "escape", void 0);
    StripeFpxBankComponent = __decorate([
        Component({
            selector: 'ngx-stripe-fpx-bank',
            template: "<div class=\"field\" #stripeElementRef></div>"
        })
    ], StripeFpxBankComponent);
    return StripeFpxBankComponent;
}());

var StripeIdealBankComponent = /** @class */ (function () {
    function StripeIdealBankComponent(stripeElementsService) {
        this.stripeElementsService = stripeElementsService;
        this.load = new EventEmitter();
        this.blur = new EventEmitter();
        this.change = new EventEmitter();
        this.focus = new EventEmitter();
        this.ready = new EventEmitter();
        this.escape = new EventEmitter();
        this.state = 'notready';
    }
    StripeIdealBankComponent.prototype.ngOnChanges = function (changes) {
        return __awaiter(this, void 0, void 0, function () {
            var options, updateElements, _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        this.state = 'starting';
                        options = this.stripeElementsService.mergeOptions(this.options, this.containerClass);
                        updateElements = false;
                        if (!(changes.elementsOptions || changes.stripe || !this.elements)) return [3 /*break*/, 2];
                        _a = this;
                        return [4 /*yield*/, this.stripeElementsService
                                .elements(this.stripe, this.elementsOptions)
                                .toPromise()];
                    case 1:
                        _a.elements = _b.sent();
                        updateElements = true;
                        _b.label = 2;
                    case 2:
                        if (changes.options ||
                            changes.containerClass ||
                            !this.element ||
                            updateElements) {
                            if (this.element && !updateElements) {
                                this.update(options);
                            }
                            else if (this.elements && updateElements) {
                                this.createElement(options);
                            }
                        }
                        this.state = 'ready';
                        return [2 /*return*/];
                }
            });
        });
    };
    StripeIdealBankComponent.prototype.ngOnInit = function () {
        return __awaiter(this, void 0, void 0, function () {
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        if (!(this.state === 'notready')) return [3 /*break*/, 2];
                        this.state = 'starting';
                        _a = this;
                        return [4 /*yield*/, this.stripeElementsService
                                .elements(this.stripe)
                                .toPromise()];
                    case 1:
                        _a.elements = _b.sent();
                        this.createElement();
                        this.state = 'ready';
                        _b.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    StripeIdealBankComponent.prototype.ngOnDestroy = function () {
        if (this.element) {
            this.element.destroy();
        }
    };
    StripeIdealBankComponent.prototype.update = function (options) {
        this.element.update(options);
    };
    /**
     * @deprecated
     */
    StripeIdealBankComponent.prototype.getIdealBank = function () {
        return this.element;
    };
    StripeIdealBankComponent.prototype.createElement = function (options) {
        var _this = this;
        if (options === void 0) { options = {}; }
        this.element = this.elements.create('idealBank', options);
        this.element.on('change', function (ev) { return _this.change.emit(ev); });
        this.element.on('blur', function () { return _this.blur.emit(); });
        this.element.on('focus', function () { return _this.focus.emit(); });
        this.element.on('ready', function () { return _this.ready.emit(); });
        this.element.on('escape', function () { return _this.escape.emit(); });
        this.element.mount(this.stripeElementRef.nativeElement);
        this.load.emit(this.element);
    };
    StripeIdealBankComponent.ctorParameters = function () { return [
        { type: StripeElementsService }
    ]; };
    __decorate([
        ViewChild('stripeElementRef', { static: false })
    ], StripeIdealBankComponent.prototype, "stripeElementRef", void 0);
    __decorate([
        Input()
    ], StripeIdealBankComponent.prototype, "containerClass", void 0);
    __decorate([
        Input()
    ], StripeIdealBankComponent.prototype, "options", void 0);
    __decorate([
        Input()
    ], StripeIdealBankComponent.prototype, "elementsOptions", void 0);
    __decorate([
        Input()
    ], StripeIdealBankComponent.prototype, "stripe", void 0);
    __decorate([
        Output()
    ], StripeIdealBankComponent.prototype, "load", void 0);
    __decorate([
        Output()
    ], StripeIdealBankComponent.prototype, "blur", void 0);
    __decorate([
        Output()
    ], StripeIdealBankComponent.prototype, "change", void 0);
    __decorate([
        Output()
    ], StripeIdealBankComponent.prototype, "focus", void 0);
    __decorate([
        Output()
    ], StripeIdealBankComponent.prototype, "ready", void 0);
    __decorate([
        Output()
    ], StripeIdealBankComponent.prototype, "escape", void 0);
    StripeIdealBankComponent = __decorate([
        Component({
            selector: 'ngx-stripe-ideal-bank',
            template: "<div class=\"field\" #stripeElementRef></div>"
        })
    ], StripeIdealBankComponent);
    return StripeIdealBankComponent;
}());

var StripeIbanComponent = /** @class */ (function () {
    function StripeIbanComponent(stripeElementsService) {
        this.stripeElementsService = stripeElementsService;
        this.load = new EventEmitter();
        this.blur = new EventEmitter();
        this.change = new EventEmitter();
        this.focus = new EventEmitter();
        this.ready = new EventEmitter();
        this.escape = new EventEmitter();
        this.state = 'notready';
    }
    StripeIbanComponent.prototype.ngOnChanges = function (changes) {
        return __awaiter(this, void 0, void 0, function () {
            var options, updateElements, _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        this.state = 'starting';
                        options = this.stripeElementsService.mergeOptions(this.options, this.containerClass);
                        updateElements = false;
                        if (!(changes.elementsOptions || changes.stripe || !this.elements)) return [3 /*break*/, 2];
                        _a = this;
                        return [4 /*yield*/, this.stripeElementsService
                                .elements(this.stripe, this.elementsOptions)
                                .toPromise()];
                    case 1:
                        _a.elements = _b.sent();
                        updateElements = true;
                        _b.label = 2;
                    case 2:
                        if (changes.options ||
                            changes.containerClass ||
                            !this.element ||
                            updateElements) {
                            if (this.element && !updateElements) {
                                this.update(options);
                            }
                            else if (this.elements && updateElements) {
                                this.createElement(options);
                            }
                        }
                        this.state = 'ready';
                        return [2 /*return*/];
                }
            });
        });
    };
    StripeIbanComponent.prototype.ngOnInit = function () {
        return __awaiter(this, void 0, void 0, function () {
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        if (!(this.state === 'notready')) return [3 /*break*/, 2];
                        this.state = 'starting';
                        _a = this;
                        return [4 /*yield*/, this.stripeElementsService
                                .elements(this.stripe)
                                .toPromise()];
                    case 1:
                        _a.elements = _b.sent();
                        this.createElement();
                        this.state = 'ready';
                        _b.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    StripeIbanComponent.prototype.ngOnDestroy = function () {
        if (this.element) {
            this.element.destroy();
        }
    };
    StripeIbanComponent.prototype.update = function (options) {
        this.element.update(options);
    };
    /**
     * @deprecated
     */
    StripeIbanComponent.prototype.getIban = function () {
        return this.element;
    };
    StripeIbanComponent.prototype.createElement = function (options) {
        var _this = this;
        if (options === void 0) { options = {}; }
        this.element = this.elements.create('iban', options);
        this.element.on('change', function (ev) { return _this.change.emit(ev); });
        this.element.on('blur', function () { return _this.blur.emit(); });
        this.element.on('focus', function () { return _this.focus.emit(); });
        this.element.on('ready', function () { return _this.ready.emit(); });
        this.element.on('escape', function () { return _this.escape.emit(); });
        this.element.mount(this.stripeElementRef.nativeElement);
        this.load.emit(this.element);
    };
    StripeIbanComponent.ctorParameters = function () { return [
        { type: StripeElementsService }
    ]; };
    __decorate([
        ViewChild('stripeElementRef', { static: false })
    ], StripeIbanComponent.prototype, "stripeElementRef", void 0);
    __decorate([
        Input()
    ], StripeIbanComponent.prototype, "containerClass", void 0);
    __decorate([
        Input()
    ], StripeIbanComponent.prototype, "options", void 0);
    __decorate([
        Input()
    ], StripeIbanComponent.prototype, "elementsOptions", void 0);
    __decorate([
        Input()
    ], StripeIbanComponent.prototype, "stripe", void 0);
    __decorate([
        Output()
    ], StripeIbanComponent.prototype, "load", void 0);
    __decorate([
        Output()
    ], StripeIbanComponent.prototype, "blur", void 0);
    __decorate([
        Output()
    ], StripeIbanComponent.prototype, "change", void 0);
    __decorate([
        Output()
    ], StripeIbanComponent.prototype, "focus", void 0);
    __decorate([
        Output()
    ], StripeIbanComponent.prototype, "ready", void 0);
    __decorate([
        Output()
    ], StripeIbanComponent.prototype, "escape", void 0);
    StripeIbanComponent = __decorate([
        Component({
            selector: 'ngx-stripe-iban',
            template: "<div class=\"field\" #stripeElementRef></div>"
        })
    ], StripeIbanComponent);
    return StripeIbanComponent;
}());

var StripeAuBankAccountComponent = /** @class */ (function () {
    function StripeAuBankAccountComponent(stripeElementsService) {
        this.stripeElementsService = stripeElementsService;
        this.load = new EventEmitter();
        this.blur = new EventEmitter();
        this.change = new EventEmitter();
        this.focus = new EventEmitter();
        this.ready = new EventEmitter();
        this.escape = new EventEmitter();
        this.state = 'notready';
    }
    StripeAuBankAccountComponent.prototype.ngOnChanges = function (changes) {
        return __awaiter(this, void 0, void 0, function () {
            var options, updateElements, _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        this.state = 'starting';
                        options = this.stripeElementsService.mergeOptions(this.options, this.containerClass);
                        updateElements = false;
                        if (!(changes.elementsOptions || changes.stripe || !this.elements)) return [3 /*break*/, 2];
                        _a = this;
                        return [4 /*yield*/, this.stripeElementsService
                                .elements(this.stripe, this.elementsOptions)
                                .toPromise()];
                    case 1:
                        _a.elements = _b.sent();
                        updateElements = true;
                        _b.label = 2;
                    case 2:
                        if (changes.options ||
                            changes.containerClass ||
                            !this.element ||
                            updateElements) {
                            if (this.element && !updateElements) {
                                this.update(options);
                            }
                            else if (this.elements && updateElements) {
                                this.createElement(options);
                            }
                        }
                        this.state = 'ready';
                        return [2 /*return*/];
                }
            });
        });
    };
    StripeAuBankAccountComponent.prototype.ngOnInit = function () {
        return __awaiter(this, void 0, void 0, function () {
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        if (!(this.state === 'notready')) return [3 /*break*/, 2];
                        this.state = 'starting';
                        _a = this;
                        return [4 /*yield*/, this.stripeElementsService
                                .elements(this.stripe)
                                .toPromise()];
                    case 1:
                        _a.elements = _b.sent();
                        this.createElement();
                        this.state = 'ready';
                        _b.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    StripeAuBankAccountComponent.prototype.ngOnDestroy = function () {
        if (this.element) {
            this.element.destroy();
        }
    };
    StripeAuBankAccountComponent.prototype.update = function (options) {
        this.element.update(options);
    };
    /**
     * @deprecated
     */
    StripeAuBankAccountComponent.prototype.getAuBankAccount = function () {
        return this.element;
    };
    StripeAuBankAccountComponent.prototype.createElement = function (options) {
        var _this = this;
        if (options === void 0) { options = {}; }
        this.element = this.elements.create('auBankAccount', options);
        this.element.on('change', function (ev) { return _this.change.emit(ev); });
        this.element.on('blur', function () { return _this.blur.emit(); });
        this.element.on('focus', function () { return _this.focus.emit(); });
        this.element.on('ready', function () { return _this.ready.emit(); });
        this.element.on('escape', function () { return _this.escape.emit(); });
        this.element.mount(this.stripeElementRef.nativeElement);
        this.load.emit(this.element);
    };
    StripeAuBankAccountComponent.ctorParameters = function () { return [
        { type: StripeElementsService }
    ]; };
    __decorate([
        ViewChild('stripeElementRef', { static: false })
    ], StripeAuBankAccountComponent.prototype, "stripeElementRef", void 0);
    __decorate([
        Input()
    ], StripeAuBankAccountComponent.prototype, "containerClass", void 0);
    __decorate([
        Input()
    ], StripeAuBankAccountComponent.prototype, "options", void 0);
    __decorate([
        Input()
    ], StripeAuBankAccountComponent.prototype, "elementsOptions", void 0);
    __decorate([
        Input()
    ], StripeAuBankAccountComponent.prototype, "stripe", void 0);
    __decorate([
        Output()
    ], StripeAuBankAccountComponent.prototype, "load", void 0);
    __decorate([
        Output()
    ], StripeAuBankAccountComponent.prototype, "blur", void 0);
    __decorate([
        Output()
    ], StripeAuBankAccountComponent.prototype, "change", void 0);
    __decorate([
        Output()
    ], StripeAuBankAccountComponent.prototype, "focus", void 0);
    __decorate([
        Output()
    ], StripeAuBankAccountComponent.prototype, "ready", void 0);
    __decorate([
        Output()
    ], StripeAuBankAccountComponent.prototype, "escape", void 0);
    StripeAuBankAccountComponent = __decorate([
        Component({
            selector: 'ngx-stripe-au-bank-account',
            template: "<div class=\"field\" #stripeElementRef></div>"
        })
    ], StripeAuBankAccountComponent);
    return StripeAuBankAccountComponent;
}());

var StripePaymentRequestButtonComponent = /** @class */ (function () {
    function StripePaymentRequestButtonComponent(stripeElementsService) {
        this.stripeElementsService = stripeElementsService;
        this.load = new EventEmitter();
        this.change = new EventEmitter();
        this.blur = new EventEmitter();
        this.focus = new EventEmitter();
        this.ready = new EventEmitter();
        this.token = new EventEmitter();
        this.paymentMethod = new EventEmitter();
        this.source = new EventEmitter();
        this.cancel = new EventEmitter();
        this.shippingaddresschange = new EventEmitter();
        this.shippingoptionchange = new EventEmitter();
        this.notavailable = new EventEmitter();
    }
    StripePaymentRequestButtonComponent.prototype.ngOnChanges = function (changes) {
        return __awaiter(this, void 0, void 0, function () {
            var options, elementsOptions, stripe, updateElements, elements;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        options = this.stripeElementsService.mergeOptions(this.options, this.containerClass);
                        elementsOptions = this.elementsOptions;
                        stripe = this.stripe;
                        updateElements = false;
                        if (!(changes.elementsOptions || changes.stripe || !this.elements)) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.stripeElementsService
                                .elements(stripe, elementsOptions)
                                .toPromise()];
                    case 1:
                        elements = _a.sent();
                        this.elements = elements;
                        updateElements = true;
                        _a.label = 2;
                    case 2:
                        if (changes.paymentOptions && this.paymentRequest) {
                            this.updateRequest(this.paymentOptions);
                        }
                        if (changes.options ||
                            changes.containerClass ||
                            !this.element ||
                            updateElements) {
                            if (this.element && !updateElements) {
                                this.update(options);
                            }
                            else if (this.elements && updateElements) {
                                this.paymentRequest = this.stripeElementsService.paymentRequest(stripe, this.paymentOptions);
                                this.paymentRequest.on('token', function (ev) { return _this.token.emit(ev); });
                                this.paymentRequest.on('paymentmethod', function (ev) {
                                    return _this.paymentMethod.emit(ev);
                                });
                                this.paymentRequest.on('source', function (ev) { return _this.source.emit(ev); });
                                this.paymentRequest.on('cancel', function () { return _this.cancel.emit(); });
                                this.paymentRequest.on('shippingaddresschange', function (ev) {
                                    return _this.shippingaddresschange.emit(ev);
                                });
                                this.paymentRequest.on('shippingoptionchange', function (ev) {
                                    return _this.shippingoptionchange.emit(ev);
                                });
                                this.element = this.elements.create('paymentRequestButton', __assign({ paymentRequest: this.paymentRequest }, options));
                                this.canMakePayment().subscribe(function (result) {
                                    if (result) {
                                        _this.element.on('click', function (ev) { return _this.change.emit(ev); });
                                        _this.element.on('blur', function () { return _this.blur.emit(); });
                                        _this.element.on('focus', function () { return _this.focus.emit(); });
                                        _this.element.on('ready', function () { return _this.ready.emit(); });
                                        _this.element.mount(_this.stripeElementRef.nativeElement);
                                        _this.load.emit({
                                            paymentRequestButton: _this.element,
                                            paymentRequest: _this.paymentRequest
                                        });
                                    }
                                    else {
                                        _this.notavailable.emit();
                                    }
                                });
                            }
                        }
                        return [2 /*return*/];
                }
            });
        });
    };
    StripePaymentRequestButtonComponent.prototype.canMakePayment = function () {
        return from(this.paymentRequest.canMakePayment());
    };
    StripePaymentRequestButtonComponent.prototype.update = function (options) {
        this.element.update(options);
    };
    StripePaymentRequestButtonComponent.prototype.updateRequest = function (options) {
        this.paymentRequest.update(options);
    };
    StripePaymentRequestButtonComponent.prototype.show = function () {
        this.paymentRequest.show();
    };
    /**
     * @deprecated
     */
    StripePaymentRequestButtonComponent.prototype.getButton = function () {
        return this.element;
    };
    StripePaymentRequestButtonComponent.ctorParameters = function () { return [
        { type: StripeElementsService }
    ]; };
    __decorate([
        ViewChild('stripeElementRef', { static: false })
    ], StripePaymentRequestButtonComponent.prototype, "stripeElementRef", void 0);
    __decorate([
        Input()
    ], StripePaymentRequestButtonComponent.prototype, "containerClass", void 0);
    __decorate([
        Input()
    ], StripePaymentRequestButtonComponent.prototype, "paymentOptions", void 0);
    __decorate([
        Input()
    ], StripePaymentRequestButtonComponent.prototype, "options", void 0);
    __decorate([
        Input()
    ], StripePaymentRequestButtonComponent.prototype, "elementsOptions", void 0);
    __decorate([
        Input()
    ], StripePaymentRequestButtonComponent.prototype, "stripe", void 0);
    __decorate([
        Output()
    ], StripePaymentRequestButtonComponent.prototype, "load", void 0);
    __decorate([
        Output()
    ], StripePaymentRequestButtonComponent.prototype, "change", void 0);
    __decorate([
        Output()
    ], StripePaymentRequestButtonComponent.prototype, "blur", void 0);
    __decorate([
        Output()
    ], StripePaymentRequestButtonComponent.prototype, "focus", void 0);
    __decorate([
        Output()
    ], StripePaymentRequestButtonComponent.prototype, "ready", void 0);
    __decorate([
        Output()
    ], StripePaymentRequestButtonComponent.prototype, "token", void 0);
    __decorate([
        Output()
    ], StripePaymentRequestButtonComponent.prototype, "paymentMethod", void 0);
    __decorate([
        Output()
    ], StripePaymentRequestButtonComponent.prototype, "source", void 0);
    __decorate([
        Output()
    ], StripePaymentRequestButtonComponent.prototype, "cancel", void 0);
    __decorate([
        Output()
    ], StripePaymentRequestButtonComponent.prototype, "shippingaddresschange", void 0);
    __decorate([
        Output()
    ], StripePaymentRequestButtonComponent.prototype, "shippingoptionchange", void 0);
    __decorate([
        Output()
    ], StripePaymentRequestButtonComponent.prototype, "notavailable", void 0);
    StripePaymentRequestButtonComponent = __decorate([
        Component({
            selector: 'ngx-stripe-payment-request-button',
            template: "<div class=\"field\" #stripeElementRef></div>"
        })
    ], StripePaymentRequestButtonComponent);
    return StripePaymentRequestButtonComponent;
}());

var StripeFactoryService = /** @class */ (function () {
    function StripeFactoryService(baseKey, baseOptions, loader, window) {
        this.baseKey = baseKey;
        this.baseOptions = baseOptions;
        this.loader = loader;
        this.window = window;
    }
    StripeFactoryService.prototype.create = function (key, options) {
        if (!key && !this.baseKey) {
            return null;
        }
        return new StripeInstance(this.loader, this.window, key || this.baseKey, options || this.baseOptions);
    };
    StripeFactoryService.ctorParameters = function () { return [
        { type: String, decorators: [{ type: Inject, args: [STRIPE_PUBLISHABLE_KEY,] }] },
        { type: undefined, decorators: [{ type: Inject, args: [STRIPE_OPTIONS,] }] },
        { type: LazyStripeAPILoader },
        { type: WindowRef }
    ]; };
    StripeFactoryService = __decorate([
        Injectable(),
        __param(0, Inject(STRIPE_PUBLISHABLE_KEY)),
        __param(1, Inject(STRIPE_OPTIONS))
    ], StripeFactoryService);
    return StripeFactoryService;
}());

var components = [
    StripeCardComponent,
    StripeCardNumberComponent,
    StripeCardExpiryComponent,
    StripeCardCvcComponent,
    StripeFpxBankComponent,
    StripeIdealBankComponent,
    StripeIbanComponent,
    StripeAuBankAccountComponent,
    StripePaymentRequestButtonComponent
];
var directives = [StripeCardGroupDirective];
var NgxStripeModule = /** @class */ (function () {
    function NgxStripeModule() {
    }
    NgxStripeModule_1 = NgxStripeModule;
    NgxStripeModule.forRoot = function (publishableKey, options) {
        return {
            ngModule: NgxStripeModule_1,
            providers: [
                LazyStripeAPILoader,
                StripeService,
                StripeFactoryService,
                StripeElementsService,
                WindowRef,
                DocumentRef,
                {
                    provide: STRIPE_PUBLISHABLE_KEY,
                    useValue: publishableKey
                },
                {
                    provide: STRIPE_OPTIONS,
                    useValue: options
                }
            ]
        };
    };
    NgxStripeModule.forChild = function (publishableKey, options) {
        return {
            ngModule: NgxStripeModule_1,
            providers: [
                LazyStripeAPILoader,
                StripeService,
                StripeFactoryService,
                StripeElementsService,
                WindowRef,
                DocumentRef,
                {
                    provide: STRIPE_PUBLISHABLE_KEY,
                    useValue: publishableKey
                },
                {
                    provide: STRIPE_OPTIONS,
                    useValue: options
                }
            ]
        };
    };
    var NgxStripeModule_1;
    NgxStripeModule = NgxStripeModule_1 = __decorate([
        NgModule({
            declarations: __spread(components, directives),
            exports: __spread(components, directives)
        })
    ], NgxStripeModule);
    return NgxStripeModule;
}());

/*
 * Public API Surface of ngx-stripe
 */

/**
 * Generated bundle index. Do not edit.
 */

export { DocumentRef, LazyStripeAPILoader, NgxStripeModule, STRIPE_OPTIONS, STRIPE_PUBLISHABLE_KEY, StripeAuBankAccountComponent, StripeCardComponent, StripeCardCvcComponent, StripeCardExpiryComponent, StripeCardGroupDirective, StripeCardNumberComponent, StripeElementsService, StripeFactoryService, StripeFpxBankComponent, StripeIbanComponent, StripeIdealBankComponent, StripeInstance, StripePaymentRequestButtonComponent, StripeService, WindowRef };
//# sourceMappingURL=ngx-stripe.js.map
